// react
import { useCallback, useEffect, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { DateTime } from 'luxon';

const FROM_PARAM = 'from';
const TO_PARAM = 'to';

function createDefaultRange({ zone = 'Europe/Copenhagen', days = 7 } = {}) {
  const nowLocal = DateTime.now().setZone(zone);
  const start = nowLocal.minus({ days }).startOf('day');
  const end = nowLocal.endOf('day');
  return [start, end];
}

function parseRangeFromParams({ fromIso, toIso, zone }) {
  const fromParam = fromIso;
  const toParam = toIso;

  if (!fromParam || !toParam) return null;

  const from = DateTime.fromISO(fromParam).setZone(zone);
  const to = DateTime.fromISO(toParam).setZone(zone);
  if (!from.isValid || !to.isValid) return null;

  return [from, to];
}

export function useUrlDateRange(options = {}) {
  const {
    fromKey = FROM_PARAM,
    toKey = TO_PARAM,
    zone = 'Europe/Copenhagen',
    defaultDays = 30,
    autoSetDefaults = true,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  const fromIso = searchParams.get(fromKey);
  const toIso = searchParams.get(toKey);
  // URL is the source of truth: derive range from URL if valid, else fallback default
  const range = useMemo(() => {
    const parsed = parseRangeFromParams({ fromIso, toIso, zone });
    if (parsed) return parsed;

    // Only create default range if autoSetDefaults is true
    if (autoSetDefaults) {
      return createDefaultRange({ zone, days: defaultDays });
    }

    // Return null range when autoSetDefaults is false and no valid URL params
    return [null, null];
  }, [fromIso, toIso, zone, defaultDays, autoSetDefaults]);

  const [from, to] = range;

  // External sync only: if params are missing/invalid, write defaults to URL (replace: true)
  // This only runs once on mount or when zone/defaultDays/keys change
  useEffect(() => {
    // Only set defaults in URL if autoSetDefaults is true
    if (!autoSetDefaults) return;

    setSearchParams(
      (prev) => {
        const currentFromIso = prev.get(fromKey);
        const currentToIso = prev.get(toKey);

        const parsed = parseRangeFromParams({
          fromIso: currentFromIso,
          toIso: currentToIso,
          zone,
        });
        if (parsed) return prev; // Already valid, nothing to do

        const [start, end] = createDefaultRange({ zone, days: defaultDays });

        const nextFromIso = start.toISO();
        const nextToIso = end.toISO();

        const next = new URLSearchParams(prev);
        next.set(fromKey, nextFromIso);
        next.set(toKey, nextToIso);
        return next;
      },
      { replace: true },
    );
  }, [zone, defaultDays, fromKey, toKey, setSearchParams, autoSetDefaults]);

  // Update function: write to URL; derived values update automatically
  const setRange = useCallback(
    (
      nextFrom,
      nextTo,
      { roundToDay = true, clampToNow = true } = {},
      { replace = false } = {},
    ) => {
      if (!nextFrom || !nextTo) return;

      let start = nextFrom.setZone(zone);
      let end = nextTo.setZone(zone);

      if (roundToDay) {
        start = start.startOf('day');
        end = end.endOf('day');
      }

      const now = DateTime.now().setZone(zone);
      if (clampToNow && end > now) {
        end = now;
      }

      if (end < start) return;

      const nextFromIso = start.toISO();
      const nextToIso = end.toISO();

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const currentFromIso = prev.get(fromKey);
          const currentToIso = prev.get(toKey);

          const same =
            currentFromIso === nextFromIso && currentToIso === nextToIso;
          if (same) return prev; // Return prev instead of undefined to prevent update
          next.set(fromKey, nextFromIso);
          next.set(toKey, nextToIso);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams, fromKey, toKey, zone],
  );
  // Derived values: UTC millis + duration
  const timeRange = useMemo(() => {
    if (!from || !to) return null;
    return {
      start: from.toUTC().toMillis(),
      end: to.toUTC().toMillis(),
    };
  }, [from, to]);

  const durationDays = useMemo(() => {
    if (!from || !to) return null;
    return Math.round(to.diff(from, 'days').days);
  }, [from, to]);
  return {
    from,
    to,
    range,
    setRange,
    timeRange,
    durationDays,
  };
}
